import type { DirectiveBinding, ObjectDirective } from "vue";

/**
 * 一键复制
 * @example <div v-copy="{ text: '需要复制的文本', fn: callback }"> 单击复制 </div>
 * @example <div v-copy.dblclick="{ text: '需要复制的文本', fn: callback }"> 双击复制 </div>
 */

interface CopyValue {
  text: string;
  fn?: (text: string) => void;
}

interface CopyHTMLElement extends HTMLElement {
  _copyFn?: () => void;
}

const bind = (el: CopyHTMLElement, binding: DirectiveBinding<CopyValue>) => {
  const { text, fn } = binding.value;
  if (el._copyFn) {
    el.removeEventListener("click", el._copyFn);
    el.removeEventListener("dblclick", el._copyFn);
  }
  el._copyFn = () => {
    handleClick(text, fn);
  };
  // 双击触发复制
  if (binding.modifiers.dblclick) {
    el.addEventListener("dblclick", el._copyFn);
  } else {
    el.addEventListener("click", el._copyFn);
  }
};

function oldHandleClick(text: string, callback?: (text: string) => void) {
  // 创建元素
  if (!document.getElementById("copyTarget")) {
    const copyTarget = document.createElement("input");
    copyTarget.setAttribute("style", "position:fixed;top:0;left:0;opacity:0;z-index:-1000;");
    copyTarget.setAttribute("id", "copyTarget");
    document.body.appendChild(copyTarget);
  }
  // 复制内容
  const input = document.getElementById("copyTarget") as HTMLInputElement;
  if (input) {
    input.value = text;
    input.select();
    document.execCommand("copy");
    if (callback) callback(text);
    input.blur();
  }
}

function handleClick(text: string, callback?: (text: string) => void) {
  if (!navigator.clipboard) {
    oldHandleClick(text, callback);
    return;
  }
  navigator.clipboard
    .writeText(text)
    .then(() => {
      if (callback) callback(text);
    })
    .catch((error) => {
      console.error("复制文本到剪贴板时发生错误:", error);
    });
}

export const vCopy: ObjectDirective<CopyHTMLElement, CopyValue> =
  process.server ?
    {
      // 服务端空实现，避免 SSR 错误
      mounted() {},
      updated() {},
      beforeUnmount() {},
    }
  : {
      // 客户端真实实现
      mounted(el, binding) {
        bind(el, binding);
      },
      updated(el, binding) {
        bind(el, binding);
      },
      beforeUnmount(el) {
        if (el._copyFn) {
          el.removeEventListener("click", el._copyFn);
          el.removeEventListener("dblclick", el._copyFn);
          delete el._copyFn;
        }
      },
    };

export default vCopy;
